import { env } from '@/lib/env';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Thin wrapper around the Command Center WebSocket bridges. Manages
 * reconnection with backoff and exposes a synchronous `connect()` /
 * `close()` surface — consumers wire onMessage callbacks themselves.
 */

export type WsMessage<T = unknown> = { channel?: string; payload: T };

type Listener<T = unknown> = (msg: WsMessage<T>) => void;

export class CommandWebSocket<T = unknown> {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener<T>>();
  private retry = 0;
  private closed = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly endpoint: 'fleet' | 'alerts') {}

  connect(): void {
    this.closed = false;
    this.open();
  }

  close(): void {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    this.socket?.close();
    this.socket = null;
  }

  on(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private open() {
    const token = useAuthStore.getState().accessToken;
    if (!token) return;
    const url = `${env.VITE_WS_URL}/api/v1/ws/${this.endpoint}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    this.socket = ws;
    ws.addEventListener('open', () => {
      this.retry = 0;
    });
    ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data) as WsMessage<T> & { type?: string };
        if (msg.type === 'ping' || msg.type === 'hello') return;
        for (const l of this.listeners) l(msg);
      } catch {
        // ignore malformed frames
      }
    });
    ws.addEventListener('close', () => {
      if (this.closed) return;
      const delay = Math.min(15000, 500 * 2 ** this.retry);
      this.retry += 1;
      this.timer = setTimeout(() => this.open(), delay);
    });
    ws.addEventListener('error', () => {
      ws.close();
    });
  }
}
